/**
 * Transforms the raw GeoJSON features from the collection hexagons endpoint into
 * UI-ready "cells" with a flat, classifier-ready stats object.
 *
 * ## The mental mapping
 *
 * The endpoint returns one Feature per populated cell, carrying only *per-object*
 * stats under `properties.detections`:
 *
 *   { [nodeUuid]: { count, mean_height, mean_object_area, sum_object_area,
 *                   estimated_stems: { low, medium, high } } }
 *
 * The model-based hexagon flow, by contrast, exposes a *cell-level* aggregate
 * (`metadata.count`, `metadata.mean_height`, …) that the pure matrix classifiers
 * read via `getPropertyValue(meta, id)`. Collections don't return that cell-level
 * block, so we derive an equivalent flat `stats` object via the shared
 * `deriveCellStats` (the same derivation the aggregate summary uses). This module
 * only unwraps the GeoJSON and delegates the number-crunching there.
 *
 * @module collectionHexagonsToCells
 */

import { deriveCellStats } from './hexagonCellStats';

/**
 * @typedef {object} HexagonCell
 * @property {number} id - stable lattice-order index (the endpoint omits ids)
 * @property {object} geometry - GeoJSON Polygon geometry (EPSG:3857)
 * @property {object} detections - raw per-class stats, kept verbatim for the
 *   info panel / summary
 * @property {object} stats - flat, classifier-ready cell aggregate (see hexagonCellStats)
 */

/**
 * @param {Array<object>} features - GeoJSON Feature array from getCollectionHexagons
 * @param {number} [resolution=1000] - hexagon area in m² (used as `stats.area`)
 * @returns {HexagonCell[]}
 */
export const collectionHexagonsToCells = (features = [], resolution = 1000) =>
	features.map((feature, index) => {
		const detections = feature?.properties?.detections ?? {};
		return {
			// Lattice order is stable for a given request and the endpoint assigns
			// no feature id, so the index is a reliable key.
			id: index,
			geometry: feature?.geometry,
			// Raw per-object stats, untouched, keyed by node uuid — the info panel
			// and summary name each object through `objectLabel`.
			detections,
			// Flat cell-level aggregate the matrix classifiers read via getPropertyValue.
			stats: deriveCellStats(detections, resolution),
		};
	});

export default collectionHexagonsToCells;
